import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/NavBar";
import { FaFacebookF, FaInstagram, FaMapMarkerAlt, FaPhone, FaTiktok, FaUserCircle, FaArrowLeft } from "react-icons/fa";
import { User, UserStatusTypes, UserVerifiedContactInfo } from "../models/auth/auth.model";
import moment from "moment";
import { useEffect, useState } from "react";
import PostAction from "../store/post/action";
import { useAppDispatch, useAppSelector } from "../store/store";
import UsersAction from "../store/users/action";
import LoadingView from "../components/LoadingView";

const BROWN = "#704e37";

interface SocialMediaUpdateState {
    name: "facebook" | "instagram" | "tiktok" | "phone";
    status: UserStatusTypes;
}

const statusConfig: Record<string, { text: string; className: string }> = {
    pending: { text: "Në pritje", className: "bg-amber-100 text-amber-800" },
    approved: { text: "Aprovuar", className: "bg-green-100 text-green-800" },
    rejected: { text: "Refuzuar", className: "bg-red-100 text-red-800" },
};

const SocialRow = ({
    label,
    value,
    href,
    icon: Icon,
    status,
    onApprove,
    onReject,
    isLoading,
    error,
}: {
    label: string;
    value: string;
    href: string;
    icon: React.ElementType;
    status: UserStatusTypes | "";
    onApprove: () => void;
    onReject: () => void;
    isLoading: boolean;
    error: string;
}) => {
    const cfg = status ? statusConfig[status] : null;
    const isPending = status === "pending";

    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => window.open(href, "_blank")}
                    className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#704e37] hover:bg-[#704e37]/5 transition shrink-0"
                >
                    <Icon className="text-xl" />
                </button>
                <div className="min-w-0">
                    <p className="font-medium text-gray-800">{label}</p>
                    <p className="text-sm text-gray-500 truncate" title={value}>{value}</p>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${cfg?.className ?? "bg-gray-200 text-gray-600"}`}>
                        {cfg?.text ?? "—"}
                    </span>
                </div>
            </div>
            {isPending && (
                <div className="flex gap-2 flex-wrap">
                    {error && <p className="text-red-600 text-sm w-full">{error}</p>}
                    {isLoading ? (
                        <LoadingView size={28} />
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={onApprove}
                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                            >
                                Aprovo
                            </button>
                            <button
                                type="button"
                                onClick={onReject}
                                className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                            >
                                Refuzo
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const UserProfile = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userParams = location.state?.user as User | undefined;

    const dispatch = useAppDispatch();
    const { isLoading: postLoading, userPosts, error: postError } = useAppSelector((s) => s.post);
    const { isLoading: usersLoading, updateUserStatusSuccess, updateVerifiedContactSuccess, error: usersError } = useAppSelector((s) => s.users);

    const [user, setUser] = useState<User | null>(userParams ?? null);
    const [showUserPosts, setShowUserPosts] = useState(false);
    const [userStatusUpdate, setUserStatusUpdate] = useState<UserStatusTypes | null>(null);
    const [socialMediaUpdate, setSocialMediaUpdate] = useState<SocialMediaUpdateState | null>(null);

    const onShowPostsPress = () => {
        if (user) {
            setShowUserPosts(true);
            PostAction.getUserPosts(user.id, dispatch);
        }
    };

    const onApproveRejectUser = (action: UserStatusTypes) => {
        if (!user) return;
        setUserStatusUpdate(action);
        UsersAction.setUserStatus(user.id, action, dispatch);
    };

    const onApproveRejectSocialMedia = (action: UserStatusTypes, social: "facebook" | "instagram" | "tiktok" | "phone") => {
        if (!user) return;
        setSocialMediaUpdate({ name: social, status: action });
        const next = { ...user.verifiedContacts, [social]: action };
        UsersAction.setUserSocialMediaStatus(user.id, next, dispatch);
    };

    const getUpdatedVerifiedContacts = (): UserVerifiedContactInfo => {
        if (!user) return {} as UserVerifiedContactInfo;
        if (socialMediaUpdate?.name === "facebook") return { ...user.verifiedContacts, facebook: socialMediaUpdate.status };
        if (socialMediaUpdate?.name === "instagram") return { ...user.verifiedContacts, instagram: socialMediaUpdate.status };
        if (socialMediaUpdate?.name === "tiktok") return { ...user.verifiedContacts, tiktok: socialMediaUpdate.status };
        if (socialMediaUpdate?.name === "phone") return { ...user.verifiedContacts, phone: socialMediaUpdate.status };
        return user.verifiedContacts;
    };

    useEffect(() => {
        if (updateUserStatusSuccess && user && userStatusUpdate) {
            UsersAction.clearState(dispatch);
            setUser({ ...user, status: userStatusUpdate });
            setUserStatusUpdate(null);
        }
    }, [updateUserStatusSuccess]);

    useEffect(() => {
        if (updateVerifiedContactSuccess && user) {
            UsersAction.clearState(dispatch);
            setUser({ ...user, verifiedContacts: getUpdatedVerifiedContacts() });
            setSocialMediaUpdate(null);
        }
    }, [updateVerifiedContactSuccess]);

    useEffect(() => {
        return () => {
            PostAction.clearState(dispatch);
            UsersAction.clearState(dispatch);
        };
    }, []);

    if (!user) {
        return (
            <div className="min-h-screen bg-[#f8f2ed] font-manrope">
                <Navbar />
                <div className="max-w-2xl mx-auto px-4 py-8">
                    <button onClick={() => navigate(-1)} className="flex items-center text-[#704e37] mb-4">
                        <FaArrowLeft className="mr-2" /> Prapa
                    </button>
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-gray-500">
                        Nuk u gjet përdoruesi.
                    </div>
                </div>
            </div>
        );
    }

    const statusCfg = statusConfig[user.status] ?? statusConfig.pending;

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-2xl mx-auto px-4 py-6">
                <button onClick={() => navigate(-1)} className="flex items-center text-[#704e37] mb-4 hover:opacity-80">
                    <FaArrowLeft className="mr-2" /> Prapa
                </button>

                {/* Profile card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                    <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-4">
                        <div className="w-20 h-20 rounded-full bg-[#704e37]/10 flex items-center justify-center shrink-0">
                            <FaUserCircle className="text-4xl" style={{ color: BROWN }} />
                        </div>
                        <div className="text-center sm:text-left flex-1">
                            <h1 className="text-2xl font-bold text-gray-900">{user.first_name} {user.last_name}</h1>
                            <p className="text-gray-600 mt-0.5">{user.email}</p>
                            {user.date_of_birth && <p className="text-gray-500 text-sm mt-1">Datëlindja: {user.date_of_birth}</p>}
                        </div>
                    </div>
                </div>

                {/* Status + actions */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Statusi i përdoruesit</h2>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium ${statusCfg.className}`}>
                                {statusCfg.text}
                            </span>
                            <span className="text-gray-500 text-sm">Krijuar më {moment(user.created_at).format("DD/MM/YYYY")}</span>
                        </div>
                        <div className="flex gap-2">
                            {usersError && <p className="text-red-600 text-sm mr-2 self-center">{usersError}</p>}
                            {usersLoading ? (
                                <LoadingView size={32} />
                            ) : (
                                <>
                                    {user.status === 'pending' && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => onApproveRejectUser("approved")}
                                                className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                                            >
                                                Aprovo
                                            </button>
                                        </>
                                    )}
                                    {(user.status === 'approved' || user.status === 'pending') && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => onApproveRejectUser("rejected")}
                                                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                                            >
                                                Refuzo
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Social media */}
                {(user.contact?.facebook || user.contact?.instagram || user.contact?.tiktok || user.contact?.phone) && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Kontaktet e verifikuara</h2>
                        <div className="space-y-3">
                            {user.contact?.facebook && (
                                <SocialRow
                                    label="Facebook"
                                    value={user.contact.facebook}
                                    href={user.contact.facebook}
                                    icon={FaFacebookF}
                                    status={user.verifiedContacts?.facebook ?? ""}
                                    onApprove={() => onApproveRejectSocialMedia("approved", "facebook")}
                                    onReject={() => onApproveRejectSocialMedia("rejected", "facebook")}
                                    isLoading={usersLoading}
                                    error={usersError ?? ""}
                                />
                            )}
                            {user.contact?.instagram && (
                                <SocialRow
                                    label="Instagram"
                                    value={user.contact.instagram}
                                    href={user.contact.instagram}
                                    icon={FaInstagram}
                                    status={user.verifiedContacts?.instagram ?? ""}
                                    onApprove={() => onApproveRejectSocialMedia("approved", "instagram")}
                                    onReject={() => onApproveRejectSocialMedia("rejected", "instagram")}
                                    isLoading={usersLoading}
                                    error={usersError ?? ""}
                                />
                            )}
                            {user.contact?.tiktok && (
                                <SocialRow
                                    label="TikTok"
                                    value={user.contact.tiktok}
                                    href={user.contact.tiktok}
                                    icon={FaTiktok}
                                    status={user.verifiedContacts?.tiktok ?? ""}
                                    onApprove={() => onApproveRejectSocialMedia("approved", "tiktok")}
                                    onReject={() => onApproveRejectSocialMedia("rejected", "tiktok")}
                                    isLoading={usersLoading}
                                    error={usersError ?? ""}
                                />
                            )}
                            {user.contact?.phone && (
                                <SocialRow
                                    label="Numri i telefonit"
                                    value={user.contact.phone}
                                    href={`tel:${user.contact.phone}`}
                                    icon={FaPhone}
                                    status={user.verifiedContacts?.phone ?? ""}
                                    onApprove={() => onApproveRejectSocialMedia("approved", "phone")}
                                    onReject={() => onApproveRejectSocialMedia("rejected", "phone")}
                                    isLoading={usersLoading}
                                    error={usersError ?? ""}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Addresses */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Adresat</h2>
                    {user.addresses?.length > 0 ? (
                        <ul className="space-y-3">
                            {user.addresses.map((address) => (
                                <li key={address.id} className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                                    <FaMapMarkerAlt className="text-[#704e37] shrink-0 mt-0.5" size={18} />
                                    <div>
                                        <p className="font-medium text-gray-900">{address.city}</p>
                                        <p className="text-gray-500 text-sm">{address.street}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-500 text-sm">Nuk ka adresa.</p>
                    )}
                </div>

                {/* Posts */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Shpalljet</h2>
                    {!showUserPosts ? (
                        <button
                            type="button"
                            onClick={onShowPostsPress}
                            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#704e37] text-white font-medium hover:opacity-90"
                        >
                            Shfaq shpalljet
                        </button>
                    ) : (
                        <>
                            {postError && <p className="text-red-600 text-sm mb-4">{postError}</p>}
                            {postLoading ? (
                                <div className="flex justify-center py-8"><LoadingView size={48} /></div>
                            ) : userPosts.length === 0 ? (
                                <p className="text-gray-500">Nuk ka shpallje.</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {userPosts.map((post) => (
                                        <div key={post.id} className="rounded-xl border border-gray-100 overflow-hidden bg-gray-50">
                                            <div className="aspect-[3/4] bg-gray-200">
                                                {post.images?.[0] ? (
                                                    <img src={post.images[0]} alt={post.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Nuk ka imazh</div>
                                                )}
                                            </div>
                                            <div className="p-3">
                                                <p className="font-medium text-gray-900 truncate">{post.name}</p>
                                                <p className="text-[#704e37] font-semibold mt-0.5">
                                                    {post.prices?.isFree ? "Falas" : `${post.prices?.sellPrice ?? 0} €`}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
